import * as v from 'valibot';

import { ENOPOrganType, ESeverity } from '#shared';

import { CLINICAL_EVENTS_DISPLAY_OPTIONS } from '../model';

export type ControlKind =
  | 'severity'
  | 'ageSteps'
  | 'slipTolerance'
  | 'display'
  | 'windowMultiplier'
  | 'none';

export interface ValueContract {
  schema: v.GenericSchema;
  rangeText: string;
  control: ControlKind;
  options: readonly string[];
}

export const EVENT_SEVERITY_PREFIX = 'eventSeverity.';

const SEVERITY_OPTIONS: readonly string[] = Object.values(ESeverity);

const severityContract: ValueContract = {
  schema: v.enum(ESeverity),
  rangeText: `One of ${SEVERITY_OPTIONS.join(', ')}`,
  control: 'severity',
  options: SEVERITY_OPTIONS,
};

const minutes = v.pipe(v.number(), v.integer(), v.minValue(1));

const ageStepsContract: ValueContract = {
  schema: v.pipe(
    v.object({
      halfFromMinutes: minutes,
      threeQuartersFromMinutes: minutes,
      fullFromMinutes: minutes,
    }),
    v.check(
      ({ halfFromMinutes, threeQuartersFromMinutes, fullFromMinutes }) =>
        halfFromMinutes < threeQuartersFromMinutes &&
        threeQuartersFromMinutes < fullFromMinutes,
    ),
  ),
  rangeText:
    'Three whole numbers of minutes, at least 1 and strictly increasing: half, three quarters, full',
  control: 'ageSteps',
  options: [],
};

const toleranceMinutes = v.pipe(v.number(), v.integer(), v.minValue(0));

const slipToleranceSchema = v.object({
  airLeg: toleranceMinutes,
  groundLeg: toleranceMinutes,
  groundStop: toleranceMinutes,
});

export type SettingsSlipTolerance = v.InferOutput<typeof slipToleranceSchema>;

const slipToleranceContract: ValueContract = {
  schema: slipToleranceSchema,
  rangeText:
    'Three whole numbers of minutes, at least 0: air leg, ground leg, ground stop',
  control: 'slipTolerance',
  options: [],
};

export const CASE_TIMELINE_ZONES = [
  'device',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
] as const;

export const CASE_TIMELINE_KINDS = [
  'any',
  'opo',
  'enteredByCustomer',
  'enteredByTmdx',
] as const;

const caseTimelineViewSchema = v.strictObject({
  filters: v.strictObject({
    organs: v.pipe(
      v.array(v.enum(ENOPOrganType)),
      v.check(organs => new Set(organs).size === organs.length),
    ),
    kind: v.picklist(CASE_TIMELINE_KINDS),
    approvalOutstanding: v.boolean(),
  }),
  groupBy: v.picklist(['none', 'aircraft', 'user']),
  lensMs: v.nullable(v.pipe(v.number(), v.integer(), v.minValue(1))),
  zone: v.picklist(CASE_TIMELINE_ZONES),
  details: v.strictObject({ eventDots: v.boolean(), context: v.boolean() }),
});

export type CaseTimelineView = v.InferOutput<typeof caseTimelineViewSchema>;

const caseTimelineViewContract: ValueContract = {
  schema: caseTimelineViewSchema,
  rangeText:
    'The per-user Case Timeline view: filters, grouping, lens, zone and details',
  control: 'none',
  options: [],
};

export const CASE_TIMELINE_VIEW_KEY = 'caseTimeline.view';
export const APPROVAL_AGE_STEPS_KEY = 'caseTimeline.approvalAgeSteps';

export const SLIP_TOLERANCE_KEY = 'caseTimeline.slipToleranceMinutes';

const displayContract: ValueContract = {
  schema: v.picklist(CLINICAL_EVENTS_DISPLAY_OPTIONS),
  rangeText: `One of ${CLINICAL_EVENTS_DISPLAY_OPTIONS.join(', ')}`,
  control: 'display',
  options: CLINICAL_EVENTS_DISPLAY_OPTIONS,
};

export const CLINICAL_EVENTS_DISPLAY_KEY = 'caseTimeline.clinicalEventsDisplay';

export const EVENT_CARD_WINDOW_MULTIPLIER_KEY = 'eventCards.windowMultiplier';

export const WINDOW_MULTIPLIER_STEPS: readonly number[] = Array.from(
  { length: 16 },
  (_, index) => (index + 1) * 0.25,
);

const windowMultiplierContract: ValueContract = {
  schema: v.picklist(WINDOW_MULTIPLIER_STEPS as [number, ...number[]]),
  rangeText: 'A multiple of 0.25 from 0.25 to 4',
  control: 'windowMultiplier',
  options: [],
};

const CONTRACTS: readonly { pattern: RegExp; contract: ValueContract }[] = [
  {
    pattern: /^eventSeverity\.[A-Za-z][A-Za-z0-9]*$/,
    contract: severityContract,
  },
  {
    pattern: /^caseTimeline\.approvalAgeSteps$/,
    contract: ageStepsContract,
  },
  {
    pattern: /^caseTimeline\.slipToleranceMinutes$/,
    contract: slipToleranceContract,
  },
  {
    pattern: /^caseTimeline\.view$/,
    contract: caseTimelineViewContract,
  },
  {
    pattern: /^caseTimeline\.clinicalEventsDisplay$/,
    contract: displayContract,
  },
  {
    pattern: /^eventCards\.windowMultiplier$/,
    contract: windowMultiplierContract,
  },
];

export const contractFor = (key: string): ValueContract | undefined =>
  CONTRACTS.find(entry => entry.pattern.test(key))?.contract;

export const checkValue = (
  key: string,
  value: unknown,
): 'ok' | 'drift' | 'unmirrored' => {
  const contract = contractFor(key);
  if (!contract) {
    return 'unmirrored';
  }
  return v.is(contract.schema, value) ? 'ok' : 'drift';
};
